// Sprint 0083 — HR config API client.
import apiClient from '@/shared/api/client'

// ── Completeness check ─────────────────────────────────────────────────────

export interface CompletenessCheck {
  name: string
  ok: boolean
  detail: string
  severity: 'BLOCKING' | 'WARNING' | 'INFO'
}

export interface CompletenessReport {
  status: 'READY' | 'WARNING' | 'BLOCKED'
  ok_count: number
  blocking_failures: number
  warning_failures: number
  total_checks: number
  checks: CompletenessCheck[]
}

export const completenessApi = {
  check: async () => (await apiClient.get<CompletenessReport>('/hr/config/completeness')).data,
}

// ── Generic config CRUD ────────────────────────────────────────────────────

function makeCrud<T extends { id?: string }>(path: string) {
  return {
    list: async (): Promise<T[]> => (await apiClient.get<T[]>(path)).data,
    create: async (body: Omit<T, 'id'>): Promise<T> =>
      (await apiClient.post<T>(path, body)).data,
    update: async (id: string, body: Partial<T>): Promise<T> =>
      (await apiClient.patch<T>(`${path}/${id}`, body)).data,
    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`${path}/${id}`)
    },
  }
}

export const attendanceRulesApi = makeCrud<{
  id?: string
  department_id?: string | null
  grace_period_minutes: number
  half_day_threshold_hours: string
  min_present_hours: string
  auto_absent_after_consecutive_days: number
  require_punch_in: boolean
  require_punch_out: boolean
  weekly_off_day_of_week: number
  effective_from: string
  effective_to?: string | null
  is_active?: boolean
  remarks?: string | null
}>('/hr/attendance-rules')

export const overtimeRulesApi = makeCrud<{
  id?: string
  department_id?: string | null
  normal_hours_per_day: string
  ot_rate_multiplier: string
  weekly_off_ot_multiplier: string
  holiday_ot_multiplier: string
  max_ot_hours_per_day: string
  ot_grace_minutes: number
  requires_pre_approval: boolean
  effective_from: string
  is_active?: boolean
  remarks?: string | null
}>('/hr/overtime-rules')

export const certificationRulesApi = makeCrud<{
  id?: string
  machine_type?: string | null
  role_required: string
  required_skill_level_min: number
  hard_block: boolean
  override_role_code?: string | null
  effective_from: string
  is_active?: boolean
  remarks?: string | null
}>('/hr/certification-rules')

export const grievanceCategoriesApi = makeCrud<{
  id?: string
  category_code: string
  category_name: string
  default_assignee_role?: string | null
  default_severity: string
  target_resolution_hours: number
  requires_witness: boolean
  requires_evidence: boolean
  is_active?: boolean
}>('/hr/grievance-categories')

export const escalationMatrixApi = makeCrud<{
  id?: string
  entity_type: string
  severity?: string | null
  level_1_after_hours: number
  level_1_to_role: string
  level_2_after_hours?: number | null
  level_2_to_role?: string | null
  level_3_after_hours?: number | null
  level_3_to_role?: string | null
  is_active?: boolean
}>('/hr/escalation-matrix')

export const kpiThresholdsApi = makeCrud<{
  id?: string
  kpi_code: string
  kpi_name: string
  category?: string | null
  green_threshold?: string | null
  yellow_threshold?: string | null
  red_threshold?: string | null
  direction: string
  unit?: string | null
  is_active?: boolean
}>('/hr/kpi-thresholds')

export const punchMappingsApi = makeCrud<{
  id?: string
  employee_id: string
  device_user_id: string
  device_id?: string | null
  is_active?: boolean
}>('/hr/punch-mappings')

// ── Worker allocation import ──────────────────────────────────────────────

export const allocationImportApi = {
  preview: async (file: File, plantId: string) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post(
      `/hr/worker-allocations/import/preview?plant_id=${plantId}`,
      fd, { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },
  commit: async (file: File, plantId: string, skip_attendance_check = true) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post(
      `/hr/worker-allocations/import/commit?plant_id=${plantId}&skip_attendance_check=${skip_attendance_check}`,
      fd, { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },
}
